import { DataSnapshot } from 'firebase/database';

import { FirebaseService } from '@lib';
import { DataBaseNodes } from '@lib/firebase';
import { DATA_EXCHANGE_TIMEOUT } from '@configs';
import { DataSourceConfig, useSiteStore } from '@stores';
import { Site, SourceUnsubscribe } from '@type';
import { logError } from '@utils';

import {
  CommunicationKey,
  DataExchangeParams,
  ExtendedTResponseType,
} from '../types';
import { GatewayUtils } from './utils';

export abstract class BaseGateway {
  protected sourceService: FirebaseService;
  protected config: DataSourceConfig;
  protected utils: GatewayUtils;

  constructor(config: DataSourceConfig) {
    this.config = config;
    this.sourceService = new FirebaseService(config);
    this.utils = new GatewayUtils(this.sourceService);
  }

  private async getPrepared(requestKeys: CommunicationKey[], siteInfo?: Site) {
    const listenerBaseQuery = `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.CommunicationChannel}`;

    const stickyNodes = await this.sourceService.readDataOnce<{
      [key: string]: ExtendedTResponseType<unknown>;
    }>(listenerBaseQuery);

    if (!stickyNodes) return;

    const responseComparator = this.utils.responseKeysComparator(
      requestKeys,
      siteInfo
    );

    const deletePromises = Object.entries(stickyNodes)
      .filter(([, node]) => responseComparator(node))
      .map(([key]) => {
        const keyToBeDeleted = `${listenerBaseQuery}/${key}`;
        return this.sourceService.removeData(keyToBeDeleted);
      });

    return await Promise.all(deletePromises);
  }

  protected async sendRequest<T>(
    siteUuid: string,
    payload: T,
    query: DataBaseNodes = DataBaseNodes.Request,
    key?: string
  ) {
    const { getSiteByUUID } = useSiteStore.getState();
    const siteInfo = getSiteByUUID(siteUuid);
    const data = {
      firebasePatientId: this.config.patientSourceUUID,
      institutionId: siteInfo.siteId,
      siteId: siteInfo.siteId,
      siteUuid,
    };

    return this.sourceService.writeData(
      query,
      {
        ...data,
        ...payload,
      },
      key
    );
  }

  protected dataExchange = async <TResponseType>(
    params: DataExchangeParams,
    timeout = DATA_EXCHANGE_TIMEOUT
  ): Promise<TResponseType> => {
    let timeoutRef: NodeJS.Timeout,
      dataHandler: (
        snapshot: DataSnapshot,
        previousChildName?: string | null
      ) => unknown,
      unsubscribe: SourceUnsubscribe;

    const listenerBaseQuery = `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.CommunicationChannel}`;

    const { getSiteByUUID } = useSiteStore.getState();
    const siteInfo = params.siteUuid
      ? getSiteByUUID(params.siteUuid)
      : undefined;

    const cleanup = async (query: string | null = null) => {
      if (query)
        await this.sourceService.removeData(`${listenerBaseQuery}/${query}`);

      if (timeoutRef) clearTimeout(timeoutRef);
      if (unsubscribe) unsubscribe();
    };

    const requestKeys: CommunicationKey[] = [];
    requestKeys.push(
      ...(Array.isArray(params.successKey)
        ? params.successKey
        : [params.successKey])
    );
    if (params.failureKey)
      requestKeys.push(
        ...(Array.isArray(params.failureKey)
          ? params.failureKey
          : [params.failureKey])
      );

    try {
      await this.getPrepared(requestKeys, siteInfo);
    } catch (error) {
      logError(`Failed to getPrepared - error=${String(error)}`);
    }

    const sendPromise = new Promise<TResponseType>((_, reject) => {
      if (params.siteUuid && params.payload) {
        this.sendRequest(
          params.siteUuid,
          params.payload,
          params.query,
          params.pushWithKey !== false
            ? this.config.patientSourceUUID
            : undefined
        ).catch(() => {
          void cleanup().finally(() => {
            reject(new Error('The send request failed'));
          });
        });
      }
    });

    const resultPromise = new Promise<TResponseType>((resolve, reject) => {
      dataHandler = snapshot => {
        if (!snapshot.exists()) {
          void cleanup(snapshot.key).finally(() => {
            reject(new Error('Value does not exist'));
          });
        }

        const data = snapshot.val() as ExtendedTResponseType<TResponseType>;

        const { successKey, failureKey } = params;

        const successResponseComparator = this.utils.responseKeysComparator(
          successKey,
          siteInfo
        );
        const failureResponseComparator = this.utils.responseKeysComparator(
          failureKey,
          siteInfo
        );

        if (successResponseComparator(data)) {
          void cleanup(snapshot.key).finally(() => {
            resolve(data);
          });
        }
        if (failureResponseComparator(data)) {
          void cleanup(snapshot.key).finally(() => {
            reject(new Error(data.message));
          });
        }
      };
      unsubscribe = this.sourceService.subscribeToChildAdded(
        listenerBaseQuery,
        dataHandler
      );
    });

    const timeoutPromise = new Promise<TResponseType>((_, reject) => {
      timeoutRef = setTimeout(() => {
        void cleanup().finally(() => {
          reject(new Error('Timeout'));
        });
      }, timeout);
    });

    return await Promise.race([sendPromise, resultPromise, timeoutPromise]);
  };
}
